import React, { useState } from 'react'
import styled from 'styled-components'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import type { FilterControlProps } from 'features/charts-v2'
// bully ignore[no-cross-feature-private-imports: same established seam AgentFilterPanel already uses for FilterControl — the charts-v2 barrel only value-exports ControlsSidebar (FilterControlProps is type-only), and ControlsSidebar's collapsible-sidebar chrome doesn't fit this inline chiclet row. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { FilterControl } from 'features/charts-v2/components/FilterControl'
import { AGENT_FILTER_OPERATOR } from 'features/trend-detection/features/agent-create/utils/agent-filter-criteria'
import type { FilterCriterion } from 'features/trend-detection/types'
import {
  expandCriterion,
  formatFilterCriterion,
  getFilterCriterionAllValues,
} from 'features/trend-detection/utils/helpers'

// AgentFilterPanel uses the same literal id for its own equivalent host —
// no shared constant exists to import (this repeats that panel's local
// const rather than introducing a cross-feature one for a two-site string).
// FilterSelectionPopover is the only popover that resolves this host, and
// only via the explicit `filterPopoverPortalTarget` prop below, never by id
// lookup — see AgentFilterPanel's doc comment for why.
const POPOVER_PORTAL_CONTAINER_ID = 'popover-portal-container'

const FilterChiclet: React.FC<{
  criterion: FilterCriterion
  filterOptions: FilterControlProps['filterOptions']
  getValueLabel: FilterControlProps['getValueLabel']
  formatted: string
  onRemove?: () => void
}> = ({ criterion, filterOptions, getValueLabel, formatted, onRemove }) => {
  const chiclet = (
    <Chiclet
      $tone="white"
      onRemove={onRemove}
      removeLabel={onRemove ? `Remove ${formatted}` : undefined}
    >
      {formatted}
    </Chiclet>
  )

  // Only the values the label itself hid behind "+N more" get a tooltip —
  // getFilterCriterionAllValues returns null on the same >2 check
  // formatFilterCriterion uses to decide whether to truncate, so a chiclet
  // that already shows every value never gets a redundant hover.
  const allValues = getFilterCriterionAllValues(
    criterion,
    filterOptions,
    getValueLabel,
  )
  if (!allValues) return chiclet

  const fieldLabel = expandCriterion(criterion, filterOptions)?.fieldLabel

  return (
    <ChicletTooltip
      content={
        <TooltipBody>
          {fieldLabel && <TooltipLabel>{fieldLabel}</TooltipLabel>}
          {allValues.map((value) => (
            <div key={value}>{value}</div>
          ))}
        </TooltipBody>
      }
    >
      <TooltipTarget>{chiclet}</TooltipTarget>
    </ChicletTooltip>
  )
}

export interface UniverseFilterChicletsProps {
  /** Raw cohort criteria — the same list `BenchmarkingAgentConfigForm`
   * already threads to its stats widget, reused here for chiclet labels via
   * `formatFilterCriterion` (the same formatter the read-only `FilterChips`
   * branch of the customer edit panel renders, so edit-mode and read-mode
   * agree on wording for the first time). */
  filters: FilterCriterion[]
  filterControlProps: FilterControlProps
}

/**
 * The Universe card's applied-filter chiclets + the `+ filter` add
 * affordance. Mounts `FilterControl` directly (not `AgentFilterPanel` —
 * every visible thing that component would contribute here is either
 * suppressed or superseded, see `BenchmarkUniversePanel`'s doc comment)
 * with `hideCardList` + `emptyStateVariant="inline"` so its ONLY rendered
 * output is the inline `+ filter` pill and the (portalled)
 * `FilterSelectionPopover` — the chiclet row itself is rendered here,
 * reusing `filterControlProps.updateFilterSelection` for both add and
 * remove so the write path is identical to what `FilterCard` uses today.
 */
export const UniverseFilterChiclets: React.FC<UniverseFilterChicletsProps> = ({
  filters,
  filterControlProps,
}) => {
  const [popoverHost, setPopoverHost] = useState<HTMLDivElement | null>(null)

  const entries = filters
    .map((criterion) => ({
      criterion,
      formatted: formatFilterCriterion(
        criterion,
        filterControlProps.filterOptions,
        filterControlProps.getValueLabel,
      ),
    }))
    .filter(({ formatted }) => !!formatted)

  const handleRemove = (field: string): void => {
    filterControlProps.updateFilterSelection(field, AGENT_FILTER_OPERATOR, [])
  }

  return (
    <div>
      {/* Unconditionally mounted, node passed directly to
          filterPopoverPortalTarget below rather than resolved by id — see
          BenchmarkUniversePanel's doc comment for the agent-type-swap
          regression an id-based lookup would reintroduce.
          Must render BEFORE the FilterControl subtree that receives it as
          `filterPopoverPortalTarget`, not after as a trailing sibling in a
          fragment — the latter shape crashed on unmount in happy-dom
          (`removeChild`: node not a child of this node) even though nothing
          ever portals into it in that state. */}
      <div id={POPOVER_PORTAL_CONTAINER_ID} ref={setPopoverHost} />
      <ChicletRow>
        {entries.map(({ criterion, formatted }) => (
          <FilterChiclet
            key={criterion.field}
            criterion={criterion}
            filterOptions={filterControlProps.filterOptions}
            getValueLabel={filterControlProps.getValueLabel}
            formatted={formatted}
            onRemove={() => handleRemove(criterion.field)}
          />
        ))}
        <FilterControl
          {...filterControlProps}
          hideCardList
          hideFilterHeaderRow
          alwaysShowAddFilters
          emptyStateLabel="+ Add filter"
          emptyStateVariant="inline"
          filterPopoverPortalTarget={popoverHost}
        />
      </ChicletRow>
    </div>
  )
}

const ChicletRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
`

// Mirrors features/trend-detection/components/scope-tooltip-styles.ts (a
// feature-private module, so not imported directly — see the file-level
// bully ignore above for why this file already avoids a second one).
const ChicletTooltip = styled(Tooltip)`
  max-width: 340px;
  max-height: 360px;
  overflow-y: auto;
`

const TooltipTarget = styled.span`
  display: inline-flex;
`

const TooltipBody = styled.div`
  font-size: 12px;
  line-height: 1.8;
`

const TooltipLabel = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
  opacity: 0.85;
`
